import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckSquare, FiUsers, FiClock, FiPieChart } from 'react-icons/fi';
import api from '../services/api';

const ManagerDashboard = () => {
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    overdueTasks: 0,
    teamMembers: 0
  });
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch tasks
      const tasksResponse = await api.get('/manager/tasks');
      const tasks = tasksResponse.data;
      const completedTasks = tasks.filter(task => task.status === 'completed').length;
      const overdueTasks = tasks.filter(task => {
        if (!task.due_date) return false;
        return new Date(task.due_date) < new Date() && task.status !== 'completed';
      }).length;
      
      // Fetch users
      const usersResponse = await api.get('/users', { params: { limit: 1000 } });
      const users = usersResponse.data.users || usersResponse.data;
      const teamMembers = users.filter(user => user.role !== 'admin').length;
      
      // Calculate team performance
      const performance = users.filter(user => user.role !== 'admin').map(user => {
        const userTasks = tasks.filter(task => 
          task.Users && task.Users.some(u => u.id === user.id)
        );
        const completedUserTasks = userTasks.filter(task => task.status === 'completed');
        const onTimeUserTasks = userTasks.filter(task => {
          if (!task.due_date || task.status !== 'completed') return false;
          return new Date(task.updated_at) <= new Date(task.due_date);
        });
        
        return {
          name: user.name,
          tasksCompleted: completedUserTasks.length,
          onTime: userTasks.length > 0 ? `${Math.round((onTimeUserTasks.length / userTasks.length) * 100)}%` : '0%'
        };
      }).sort((a, b) => b.tasksCompleted - a.tasksCompleted);
      
      setStats({
        totalTasks: tasks.length,
        completedTasks,
        pendingTasks: tasks.length - completedTasks,
        overdueTasks,
        teamMembers
      });
      
      setTeamPerformance(performance);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-secondary-900 mb-6">Manager Dashboard</h1>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary-100 text-primary-600">
              <FiCheckSquare size={24} />
            </div>
            <div className="ml-4">
              <h2 className="text-sm font-medium text-secondary-500">Tasks</h2>
              <p className="text-2xl font-semibold text-secondary-900">{stats.totalTasks}</p>
              <div className="mt-1 text-xs">
                <span className="text-success">{stats.completedTasks} completed</span>
                {' • '}
                <span className="text-secondary-500">{stats.pendingTasks} pending</span>
                {' • '}
                <span className="text-danger">{stats.overdueTasks} overdue</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary-100 text-primary-600">
              <FiUsers size={24} />
            </div>
            <div className="ml-4">
              <h2 className="text-sm font-medium text-secondary-500">Team Members</h2>
              <p className="text-2xl font-semibold text-secondary-900">{stats.teamMembers}</p>
              <div className="mt-1 text-xs text-secondary-500">
                <Link to="/manager/team" className="text-primary-600 hover:underline">Manage team</Link>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary-100 text-primary-600">
              <FiPieChart size={24} />
            </div>
            <div className="ml-4">
              <h2 className="text-sm font-medium text-secondary-500">Completion Rate</h2>
              <p className="text-2xl font-semibold text-secondary-900">
                {Math.round((stats.completedTasks / stats.totalTasks) * 100)}%
              </p>
              <div className="mt-1 text-xs text-secondary-500">
                This week
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Team Performance */}
      <div className="bg-white rounded-lg shadow-card p-6 mb-8">
        <h2 className="text-lg font-medium text-secondary-900 mb-4">Team Performance</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Team Member
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Tasks Completed
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  On-Time Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {loading ? (
                <tr>
                  <td colSpan="3" className="px-4 py-8 text-center text-secondary-500">
                    Loading team performance...
                  </td>
                </tr>
              ) : teamPerformance.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-4 py-8 text-center text-secondary-500">
                    No team performance data available
                  </td>
                </tr>
              ) : (
                teamPerformance.slice(0, 5).map((member, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-secondary-900">
                      {member.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-500">
                      {member.tasksCompleted}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-500">
                      {member.onTime}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-card p-6">
        <h2 className="text-lg font-medium text-secondary-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/manager/tasks" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
            <h3 className="font-medium">Assign Tasks</h3>
            <p className="text-sm text-secondary-500">Create and assign new tasks to team members</p>
          </Link>
          <Link to="/manager/project-tasks" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
            <h3 className="font-medium">Project Tasks</h3>
            <p className="text-sm text-secondary-500">Manage tasks by project</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;